import React, { useMemo } from 'react';
import { ComposableDevicesManagerStoreInfoResponse } from '../../../../service/device/hooks/useDevicesManager';
import useWatchError from '../../../../shared/hooks/useWatchError';
import { Skeleton } from '@mui/material';
import { useAppSelector } from '../../../../redux/store.model';
import { getGroupFromGroupIdSelector } from '../../../../redux/structure/selectors/Group.selector';
import { useParams } from 'react-router-dom';
import { toBase64 } from '../../../../shared/util/base64';
import InputSelect from '../../../../theme/ui/Atoms/InputSelect';
import { useTranslation } from 'react-i18next';
import useMachineTypesByWorkspaceId from '../../../../redux/workspace/hooks/useMachineTypeByWorkspaceId';
interface DeviceTypeProps {
  device: ComposableDevicesManagerStoreInfoResponse;
  isEditing?: boolean;
  onSelectType?: (type: string) => void;
  formik?: any;
}
function DeviceType(props: DeviceTypeProps) {
  const { device, isEditing, onSelectType, formik } = props;
  const { marketId } = useParams<{ marketId: string }>();
  const {
    data: machineTypes,
    isLoading: isLoadingMachineTypes,
    error: errorMachineTypes,
  } = useMachineTypesByWorkspaceId(marketId ?? '');
  useWatchError(errorMachineTypes);

  const { t } = useTranslation();

  const group = useAppSelector((state) =>
    getGroupFromGroupIdSelector(state, {
      workspaceId: marketId,
      groupId: toBase64(device.composableDeviceId ?? ''),
    })
  );

  const canEditDeviceType = useMemo(
    () => device?.composableDevice?.labels['node.nodeType'] === '2',
    [device?.composableDevice?.labels]
  );

  const deviceTypeOptions = useMemo(
    () =>
      machineTypes
        ?.filter((machineType) => machineType.labels.nodeType === '2')
        .map((machineType) => ({
          value: machineType.id,
          text: machineType.name,
        })),
    [machineTypes]
  );

  const machineTypeId =
    device.composableDevice?.machineTypeId ??
    group?.composableDevice.machineTypeId;

  const machineTypeName = useMemo(
    () =>
      machineTypes?.find((machineType) => machineType.id === machineTypeId)
        ?.name,
    [machineTypes, machineTypeId]
  );

  const value = machineTypeName || device.model;
  if (isLoadingMachineTypes) {
    return (
      <span>
        <Skeleton variant="text" width={50} />
      </span>
    );
  }
  return (
    <span>
      {isEditing && canEditDeviceType ? (
        <InputSelect
          select
          id={`device-type-select-${device.uuid}`}
          name={`device-type-select-${device.uuid}`}
          options={deviceTypeOptions}
          disable={isLoadingMachineTypes}
          placeholder={t('initial_setup.scan_air_connect.device_type')}
          fullWidth
          required
          aria-required
          value={formik.values.machineTypeId}
          onChange={(v: string) => {
            if (onSelectType) {
              onSelectType(v);
            }
          }}
          onBlur={formik.handleBlur}
        />
      ) : (
        value
      )}
    </span>
  );
}

export default DeviceType;
